import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../firebase';
import JobCardList from './JobCardList';
import PhotoUploader from '../../Photos/PhotoUploader';
import PhotoGallery from '../../Photos/PhotoGallery';
import { usePhotos } from '../../../hooks/usePhotos';

const VehicleDetails = ({ vehicle }) => {
  const [jobCards, setJobCards] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  const { photos, loading, addPhotos, removePhoto } = usePhotos('vehicle', vehicle.id);

  useEffect(() => {
    // Fetch the job cards for the selected vehicle
    const fetchJobCards = async () => {
      const q = query(
        collection(db, 'jobs'),
        where('vehicleId', '==', vehicle.id)
      );
      const jobCardSnapshot = await getDocs(q);
      const jobCardList = jobCardSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setJobCards(jobCardList);
    };

    if (vehicle) {
      fetchJobCards();
    }
  }, [vehicle]);

  const handleUploadComplete = async (uploadedPhotos) => {
    await addPhotos(uploadedPhotos);
  };

  const handlePhotosUpdate = async (updatedPhotos) => {
    const removedPhoto = photos.find(
      p => !updatedPhotos.find(up => up.storagePath === p.storagePath)
    );

    if (removedPhoto) {
      await removePhoto(removedPhoto);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      {/* Vehicle Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mr-4">
              <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white mb-1">
                {vehicle.vehicleMake} {vehicle.vehicleModel}
              </h2>
              <p className="text-blue-100 font-medium">
                Registration: {vehicle.registrationNumber}
              </p>
            </div>
          </div>
          <div className="hidden sm:block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-xl">
            <p className="text-xs text-blue-100 uppercase font-medium">Job Cards</p>
            <p className="text-2xl font-bold text-white">{jobCards.length}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-gray-50 px-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('details')}
            className={`${
              activeTab === 'details'
                ? 'border-indigo-600 text-indigo-600 bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-4 border-b-2 font-semibold text-sm transition-all duration-200 rounded-t-lg flex items-center`}
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Vehicle Details
          </button>
          <button
            onClick={() => setActiveTab('photos')}
            className={`${
              activeTab === 'photos'
                ? 'border-indigo-600 text-indigo-600 bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-4 border-b-2 font-semibold text-sm transition-all duration-200 rounded-t-lg flex items-center`}
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Photos
            <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-600 rounded-full text-xs font-bold">
              {photos.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('jobCards')}
            className={`${
              activeTab === 'jobCards'
                ? 'border-indigo-600 text-indigo-600 bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-4 border-b-2 font-semibold text-sm transition-all duration-200 rounded-t-lg flex items-center`}
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Job Cards
            <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-600 rounded-full text-xs font-bold">
              {jobCards.length}
            </span>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'details' && (
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-blue-100">
                <p className="text-xs font-semibold text-blue-700 uppercase mb-2">Vehicle Make</p>
                <p className="text-lg font-bold text-gray-900">{vehicle.vehicleMake}</p>
              </div>

              <div className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
                <p className="text-xs font-semibold text-indigo-700 uppercase mb-2">Vehicle Model</p>
                <p className="text-lg font-bold text-gray-900">{vehicle.vehicleModel}</p>
              </div>

              <div className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-100">
                <p className="text-xs font-semibold text-emerald-700 uppercase mb-2">Registration Number</p>
                <p className="text-lg font-bold text-gray-900">{vehicle.registrationNumber}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                <p className="text-xs font-semibold text-purple-700 uppercase mb-2">VIN</p>
                <p className="text-sm font-mono font-bold text-gray-900 break-all">{vehicle.VIN}</p>
              </div>

              <div className="p-4 bg-gradient-to-br from-rose-50 to-orange-50 rounded-xl border border-rose-100">
                <p className="text-xs font-semibold text-rose-700 uppercase mb-2">GIN</p>
                <p className="text-sm font-mono font-bold text-gray-900 break-all">{vehicle.GIN}</p>
              </div>

              <div className="p-4 bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl border border-amber-100">
                <p className="text-xs font-semibold text-amber-700 uppercase mb-2">Number of Visits</p>
                <div className="flex items-center">
                  <svg className="w-6 h-6 text-amber-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <p className="text-3xl font-bold text-gray-900">{jobCards.length}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'photos' && (
        <div className="space-y-6">
          {/* Legal Protection Notice */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Legal Protection - Vehicle Documentation
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    Document the vehicle condition at onboarding to protect against false damage claims.
                    Upload photos from multiple angles and any existing damage.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Photo Uploader */}
          <PhotoUploader
            entityType="vehicle"
            entityId={vehicle.id}
            onUploadComplete={handleUploadComplete}
            existingPhotos={photos}
            maxPhotos={20}
            categories={[
              'Front View',
              'Rear View',
              'Left Side',
              'Right Side',
              'Dashboard',
              'Interior - Front',
              'Interior - Rear',
              'Engine Bay',
              'Trunk/Boot',
              'Existing Damage',
              'Wheels/Tires',
              'VIN Plate',
              'Other'
            ]}
          />

          {/* Photo Gallery */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading photos...</p>
            </div>
          ) : (
            <PhotoGallery
              entityType="vehicle"
              entityId={vehicle.id}
              photos={photos}
              onPhotosUpdate={handlePhotosUpdate}
              readOnly={false}
            />
          )}
        </div>
      )}

      {activeTab === 'jobCards' && (
        <div className="p-6">
          {jobCards.length > 0 ? (
            <JobCardList jobCards={jobCards} />
          ) : (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <p className="text-gray-600 font-semibold text-lg mb-2">No Job Cards Found</p>
              <p className="text-gray-500">This vehicle has no service history yet</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VehicleDetails;
